import React, { useState, useRef } from "react";
import ComingSoonBadge from "./ComingSoonBadge";
import { uploadMedia } from "../../../features/media/utils/mediaUploadUtils";
import { CognitoAuthService } from "../../../services/cognitoService";
import { debug } from "../../../utils/debugLogger";

interface ConsentFormProps {
  customerId?: string;
  idToken: string;
  userName?: string;
  onConsentComplete: (consentVideoUrl: string) => void;
  onBack: () => void;
}

const ConsentForm: React.FC<ConsentFormProps> = ({
  customerId,
  idToken,
  userName = "[Your Full Name]",
  onConsentComplete,
  onBack,
}) => {
  const [consentVideo, setConsentVideo] = useState<File | null>(null);
  const [videoPreviewUrl, setVideoPreviewUrl] = useState<string>("");
  const [isUploading, setIsUploading] = useState(false);
  const [acknowledgments, setAcknowledgments] = useState({
    readScript: false,
    verificationOnly: false,
    agreeToTerms: false,
  });
  const [dragActive, setDragActive] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [mediaStream, setMediaStream] = useState<MediaStream | null>(null);
  const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder | null>(
    null
  );
  const [recordedChunks, setRecordedChunks] = useState<Blob[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const videoPreviewRef = useRef<HTMLVideoElement>(null);

  const consentStatement = `I, ${userName}, authorize this service to create an AI avatar of me. I understand and agree to the Terms of Service.`;

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      if (file.type.startsWith("video/")) {
        setConsentVideo(file);
        const url = URL.createObjectURL(file);
        setVideoPreviewUrl(url);
      } else {
        alert("Please upload a video file");
      }
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setConsentVideo(file);
      const url = URL.createObjectURL(file);
      setVideoPreviewUrl(url);
    }
  };

  const startRecording = async () => {
    try {
      // Request camera and microphone access
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 1280 },
          height: { ideal: 720 },
          facingMode: "user",
        },
        audio: true,
      });

      setMediaStream(stream);

      // Set stream to video element for preview
      if (videoPreviewRef.current) {
        videoPreviewRef.current.srcObject = stream;
        videoPreviewRef.current.play();
      }

      // Create MediaRecorder
      const options = { mimeType: "video/webm;codecs=vp9" };
      let recorder: MediaRecorder;

      try {
        recorder = new MediaRecorder(stream, options);
      } catch (e) {
        // Fallback to default mimeType if vp9 is not supported
        recorder = new MediaRecorder(stream);
      }

      const chunks: Blob[] = [];
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunks.push(e.data);
        }
      };

      recorder.onstop = () => {
        const blob = new Blob(chunks, { type: "video/webm" });
        const file = new File([blob], `consent-video-${Date.now()}.webm`, {
          type: "video/webm",
        });
        setConsentVideo(file);
        const url = URL.createObjectURL(blob);
        setVideoPreviewUrl(url);
        setRecordedChunks([]);
      };

      recorder.start();
      setMediaRecorder(recorder);
      setIsRecording(true);
    } catch (error) {
      debug.error("Error accessing camera/microphone:", error);
      alert(
        "Unable to access camera/microphone. Please check your permissions and try again."
      );
    }
  };

  const stopRecording = () => {
    if (mediaRecorder && isRecording) {
      mediaRecorder.stop();
      setIsRecording(false);
    }

    if (mediaStream) {
      mediaStream.getTracks().forEach((track) => track.stop());
      setMediaStream(null);
    }

    if (videoPreviewRef.current) {
      videoPreviewRef.current.srcObject = null;
    }
  };

  // Cleanup on unmount
  React.useEffect(() => {
    return () => {
      if (mediaStream) {
        mediaStream.getTracks().forEach((track) => track.stop());
      }
      if (videoPreviewUrl) {
        URL.revokeObjectURL(videoPreviewUrl);
      }
    };
  }, [mediaStream, videoPreviewUrl]);

  const handleUpload = async () => {
    if (!consentVideo) {
      alert("Please record or upload a consent video");
      return;
    }

    if (
      !acknowledgments.readScript ||
      !acknowledgments.verificationOnly ||
      !acknowledgments.agreeToTerms
    ) {
      alert("Please acknowledge all statements");
      return;
    }

    setIsUploading(true);
    try {
      const userEmail = CognitoAuthService.getUserEmail(idToken);
      // Use customerId if provided, otherwise use a default or user-based ID
      const uploadCustomerId = customerId || "default";

      const result = await uploadMedia({
        file: consentVideo,
        customerId: uploadCustomerId,
        userEmail,
        token: idToken,
        isLogo: false,
        mediaType: "processed",
        additionalMetadata: {
          type: "consent_video",
          consent_statement: consentStatement,
          acknowledgments: acknowledgments,
        },
        tags: ["consent", "avatar"],
      });

      if (result.success && result.cloudFrontUrl) {
        onConsentComplete(result.cloudFrontUrl);
      } else {
        throw new Error(
          result.errorMessage || "Failed to upload consent video"
        );
      }
    } catch (error) {
      debug.error("Error uploading consent video:", error);
      alert("Failed to upload consent video. Please try again.");
    } finally {
      setIsUploading(false);
    }
  };

  const allAcknowledged =
    acknowledgments.readScript &&
    acknowledgments.verificationOnly &&
    acknowledgments.agreeToTerms;

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
      <div className="flex items-center gap-2 mb-4">
        <h3 className="text-lg font-bold text-gray-800">
          Consent Verification
        </h3>
        <ComingSoonBadge size="sm" />
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-6">
        <p className="text-sm text-blue-800 flex items-start gap-2">
          <span className="material-icons text-base">info</span>
          <span>
            <strong>Note:</strong> This feature is in active development. Some
            advanced features like video editing and enhanced verification are
            coming soon.
          </span>
        </p>
      </div>

      <p className="text-sm text-gray-600 mb-6">
        To create your AI avatar, we need a short video of you reading the
        consent statement below.
      </p>

      <div className="mb-6">
        <h4 className="text-sm font-semibold text-gray-700 mb-2">
          Your Consent Statement
        </h4>
        <p className="text-xs text-gray-500 mb-3">
          Please record yourself clearly reading the following statement aloud.
          This is required to authorize the use of your likeness for the AI
          avatar.
        </p>
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <p className="text-sm text-gray-700 font-medium">
            {consentStatement}
          </p>
        </div>
      </div>

      {/* Video Upload Area */}
      <div className="mb-6">
        <div
          className={`border-2 border-dashed rounded-lg p-6 mb-4 transition-colors ${
            dragActive
              ? "border-[#2f4941] bg-[#2f4941]/5"
              : "border-gray-300 bg-gray-50"
          }`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          {consentVideo && videoPreviewUrl ? (
            <div className="relative">
              <video
                ref={videoPreviewRef}
                src={videoPreviewUrl}
                controls
                className="w-full max-h-64 rounded-lg"
              >
                Your browser does not support the video tag.
              </video>
              <button
                onClick={() => {
                  setConsentVideo(null);
                  if (videoPreviewUrl) {
                    URL.revokeObjectURL(videoPreviewUrl);
                  }
                  setVideoPreviewUrl("");
                }}
                className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
              >
                <span className="material-icons text-sm">close</span>
              </button>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center text-center">
              <span className="material-icons text-5xl text-gray-400 mb-3">
                videocam
              </span>
              <p className="font-semibold text-gray-700 mb-1">
                Record or Upload Video
              </p>
              <p className="text-sm text-gray-500 mb-4">
                Drag & drop your video file here, or click a button below
              </p>
              <div className="flex gap-3">
                {!isRecording ? (
                  <button
                    onClick={startRecording}
                    className="flex items-center justify-center gap-2 px-4 py-2 bg-[#2f4941] hover:bg-[#1e3128] text-white rounded-lg text-sm font-bold transition-colors"
                  >
                    <span className="material-icons text-lg">
                      fiber_manual_record
                    </span>
                    <span>Record</span>
                  </button>
                ) : (
                  <button
                    onClick={stopRecording}
                    className="flex items-center justify-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-bold transition-colors"
                  >
                    <span className="material-icons text-lg">stop</span>
                    <span>Stop Recording</span>
                  </button>
                )}
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="flex items-center justify-center gap-2 px-4 py-2 bg-surface-ui hover:bg-surface-ui-hover text-gray-800 rounded-lg text-sm font-bold transition-colors"
                >
                  <span className="material-icons text-lg">file_upload</span>
                  <span>Upload</span>
                </button>
              </div>
            </div>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="video/*"
            onChange={handleFileChange}
            className="hidden"
          />
        </div>
      </div>

      {/* Acknowledgment Checkboxes */}
      <div className="mb-6">
        <h4 className="text-sm font-semibold text-gray-700 mb-3">
          Please Acknowledge
        </h4>
        <div className="space-y-3">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={acknowledgments.readScript}
              onChange={(e) =>
                setAcknowledgments({
                  ...acknowledgments,
                  readScript: e.target.checked,
                })
              }
              className="mt-1 w-4 h-4 text-[#2f4941] border-gray-300 rounded focus:ring-[#2f4941]"
            />
            <span className="text-sm text-gray-700">
              I have read the script out loud in my video.
            </span>
          </label>
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={acknowledgments.verificationOnly}
              onChange={(e) =>
                setAcknowledgments({
                  ...acknowledgments,
                  verificationOnly: e.target.checked,
                })
              }
              className="mt-1 w-4 h-4 text-[#2f4941] border-gray-300 rounded focus:ring-[#2f4941]"
            />
            <span className="text-sm text-gray-700">
              I understand this video is for verification purposes only.
            </span>
          </label>
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={acknowledgments.agreeToTerms}
              onChange={(e) =>
                setAcknowledgments({
                  ...acknowledgments,
                  agreeToTerms: e.target.checked,
                })
              }
              className="mt-1 w-4 h-4 text-[#2f4941] border-gray-300 rounded focus:ring-[#2f4941]"
            />
            <span className="text-sm text-gray-700">
              I agree to the{" "}
              <a
                href="/terms-of-service"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#2f4941] hover:underline"
              >
                Terms of Service
              </a>
              .
            </span>
          </label>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-between pt-2">
        <button
          onClick={onBack}
          className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
        >
          Back
        </button>
        <button
          onClick={handleUpload}
          disabled={!consentVideo || !allAcknowledged || isUploading}
          className="flex items-center justify-center gap-2 px-6 py-2 h-11 bg-[#2f4941] hover:bg-[#1e3128] text-white rounded-lg text-sm font-bold tracking-[0.015em] disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed transition-colors"
        >
          {isUploading ? (
            <>
              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="none"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              <span>Uploading...</span>
            </>
          ) : (
            <>
              <span>Continue</span>
              <span className="material-icons text-lg">arrow_forward</span>
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default ConsentForm;
